/**
 * Individual partner (Viator) orders — `GET /hotels/:hotelId/partners/orders`,
 * `GET /tenants/:tenantId/partners/orders`, `GET /admin/partners/orders`.
 * Contract confirmed 2026-09-27 (backend dev@8379566): a booking made
 * outside the hotel's configured catalog still lands here, flagged
 * `outOfCatalog`, with `serviceId` null (no catalog service to link to) but
 * `partnerProductName`/`partnerProductCode` always present. `?catalog=in|out|all`
 * filters the list (default all; any other value is a 400).
 *
 * Money fields corrected 2026-09-27 after sim hit a white screen (dev,
 * admin@66572170): the real response carries `totalGuestPrice`/
 * `hotelFeeAmount`/`hotelNetPrice`/`hotelFeePercent`, not `amount`/
 * `commission`/`currency`. `hotelFeeAmount`/`hotelFeePercent` are absent at
 * hotel scope (no commission shown there) — always optional here.
 */
/** `pending`: not yet earned (service in the future or unconfirmed). `earned`/`cancelled` are terminal. */
export type PartnerOrderEarningState = "pending" | "earned" | "cancelled";

/**
 * `room`: booked from a room's own QR/campaign code. `hotel_wide`: booked
 * from the reception/hotel-wide QR, not tied to a specific room (task 0041,
 * contract confirmed on dev e4fc7ce5, 2026-09-30).
 */
export type PartnerAttributionSource = "room" | "hotel_wide";

export interface PartnerOrderRecord {
  id: string;
  hotelId: string;
  /** Not in the confirmed contract — filled in only where the caller already knows it. */
  hotelName?: string;
  serviceId: string | null;
  partnerProductCode: string;
  partnerProductName: string;
  outOfCatalog: boolean;
  /**
   * Room attribution (task 0041, contract 2026-09-30): which QR/campaign
   * code the booking came from. `roomId`/`roomNumber` are `null` for
   * `hotel_wide` orders and for a `room`-sourced order whose campaign code
   * no longer matches a known room ("Unknown room" — show `campaignRoomCode`
   * instead). Absent (not just `null`) on orders imported before this
   * contract — always optional here.
   */
  attributionSource?: PartnerAttributionSource;
  roomId?: string | null;
  roomNumber?: string | null;
  campaignRoomCode?: string | null;
  totalGuestPrice: number;
  hotelNetPrice: number;
  /** Absent at hotel scope — commission is a tenant/admin-level figure. */
  hotelFeeAmount?: number;
  hotelFeePercent?: number;
  /** Tenant/hotel scope (tasks 0028/0029, contract 2026-09-28) — never a rate. */
  totalBooked?: number;
  earnings?: number;
  /**
   * True when the partner never returned an exact amount and this order's
   * money fields are an estimate (task 0031, contract 2026-09-29). Every
   * amount on this record should show as approximate, not just one field.
   */
  amountEstimated?: boolean;
  /** When the activity happens — "Service date", the list's primary sort (task 0033, contract 2026-09-29). */
  travelDate: string;
  /** When the guest made the booking — "Booked on". Distinct from `createdAt` (our own ingestion time). */
  bookingDate: string;
  /** Guest count — "Guests" column. */
  travellers: number;
  /** Who cancelled the booking, `null` unless `earningState === "cancelled"`. */
  cancelledBy: string | null;
  earningState: PartnerOrderEarningState;
  /** Admin-only — for reconciliation against the Viator report, never shown to tenant/hotel. */
  providerCommissionAmount?: number;
  providerCommissionCurrency?: string;
  /** Admin-only — when the booking report row that last touched this order was imported, `null` if none. */
  reportImportedAt?: string | null;
  createdAt: string;
}

export const PartnerOrdersCatalogFilter = {
  IN: "in",
  OUT: "out",
  ALL: "all",
} as const;

export type PartnerOrdersCatalogFilter =
  (typeof PartnerOrdersCatalogFilter)[keyof typeof PartnerOrdersCatalogFilter];

export interface PartnerOrdersQuery {
  /** By service date (`travelDate`), not booking/creation date (task 0033, contract 2026-09-29). */
  from: string;
  to: string;
  page?: number;
  limit?: number;
  /** Defaults to "all" server-side when omitted. */
  catalog?: PartnerOrdersCatalogFilter;
  /** Task 0041, contract 2026-09-30 — omit for "all sources". */
  attribution?: PartnerAttributionSource;
  /** Task 0041 — filters to one specific room's orders (implies `attribution=room`). */
  roomId?: string;
}

export interface PartnerOrdersResponse {
  data: PartnerOrderRecord[];
  total: number;
}

/**
 * Admin/global-summary scope only — carries the platform's own commission
 * figures. Never used for a tenant- or hotel-facing summary (tasks
 * 0028/0029, contract 2026-09-28: tenant/hotel see earnings, never a rate
 * or a Guesteria-side amount) — see `PartnerHotelOutOfCatalogSummary` for
 * that scope instead.
 */
export interface PartnerOutOfCatalogSummary {
  count: number;
  revenue: number;
  commission: number;
}

/**
 * Tenant/hotel scope — earnings-only, no commission (contract 2026-09-27
 * for hotel scope, extended to tenant scope 2026-09-28 — `TenantPartnerOrdersSummary`
 * below now uses this same type instead of the commission-bearing one).
 */
export interface PartnerHotelOutOfCatalogSummary {
  count: number;
  revenue: number;
  booked: number;
  /** Earned: the hotel's share of confirmed out-of-catalog orders. */
  earnings: number;
  /** Expected: the hotel's share of out-of-catalog orders still estimated; absent on older backends. */
  pendingEarnings?: number;
}

/**
 * `GET /tenants/:tenantId/partners/orders/summary`. `totalBooked`/`earnings`/
 * `cancelledOrders`/`refundedAmount` and the earnings-only `outOfCatalog`
 * added 2026-09-28 (tasks 0028/0029) — no rate or Guesteria-side amount at
 * this scope, matching `HotelPartnerOrdersSummary` (`types/hotelService.ts`).
 */
export interface TenantPartnerOrdersSummary {
  totalOrders: number;
  /** Booked: guest price of the orders, not income (DV-68). */
  totalRevenue: number;
  /** Partner revenue: commission, earned + expected. Show this as income. */
  partnerRevenue: number;
  totalBooked: number;
  earnings: number;
  currency: string;
  cancelledOrders: number;
  /** Count of REFUNDED orders (DV-72); absent on older backends. */
  refundedOrders?: number;
  refundedAmount: number;
  /** Orders whose amounts are estimates, not exact partner figures (task 0031, contract 2026-09-29). */
  estimatedOrders: number;
  /**
   * Not yet earned — the "Expected" tile (task 0033, contract 2026-09-29).
   * `earnings` above is the "Earned" tile; together they're the full
   * picture now that orders carry `earningState`.
   */
  pendingEarnings: number;
  pendingOrders: number;
  outOfCatalog: PartnerHotelOutOfCatalogSummary;
}

/**
 * `GET /admin/partners/orders/summary` — across every tenant, Guesteria
 * staff only. `totalBooked`/`earnings`/`guesteriaCommission`/`guesteriaNet`
 * added 2026-09-28 (task 0029, the "Глобальный summary Guesteria") — this
 * scope keeps full commission visibility, unlike tenant/hotel.
 */
export interface AdminPartnerOrdersSummary {
  totalOrders: number;
  totalRevenue: number;
  totalBooked: number;
  earnings: number;
  guesteriaCommission: number;
  guesteriaNet: number;
  currency: string;
  /** Cancelled orders: in neither `totalOrders` nor `totalRevenue` (DV-72). */
  cancelledOrders: number;
  /** Orders whose amounts are estimates, not exact partner figures (task 0031, contract 2026-09-29). */
  estimatedOrders: number;
  /**
   * Not yet earned — the "Expected" counterpart to `earnings` (task 0038 sim
   * fix, 2026-09-30). Missing from this type until now even though its
   * `pendingGuesteriaCommission`/`pendingGuesteriaNet` siblings were already
   * here — the admin summary page had an Earned tile but no Expected one.
   */
  pendingEarnings: number;
  /** Not yet earned — the pending counterpart to `guesteriaCommission`/`guesteriaNet` (task 0033, contract 2026-09-29). */
  pendingGuesteriaCommission: number;
  pendingGuesteriaNet: number;
  outOfCatalog: PartnerOutOfCatalogSummary;
}

// --- Booking report import (task 0033, contract 2026-09-29). Guesteria
// staff only, from the Viator provider page.

export interface PartnerOrdersImportSkippedRow {
  ref: string;
  line: number;
  reason: string;
}

/** `POST /admin/partners/orders/import` (multipart, field `file`, ≤10MB). 400 on a bad file — show the response text. */
export interface PartnerOrdersImportResponse {
  importId: string;
  rows: number;
  created: number;
  updated: number;
  skipped: PartnerOrdersImportSkippedRow[];
  importedAt: string;
}

export type PartnerOrdersImportSource = "upload" | "robot";

/** One row of `GET /admin/partners/orders/imports?limit=20`. `source: "robot"` shows as "Auto (daily)". */
export interface PartnerOrdersImportHistoryEntry {
  id: string;
  provider: string;
  source: PartnerOrdersImportSource;
  fileName: string | null;
  importedByEmail: string | null;
  rows: number;
  created: number;
  updated: number;
  /** Count — see `skippedRows` for the per-row detail. */
  skipped: number;
  skippedRows: PartnerOrdersImportSkippedRow[];
  createdAt: string;
}
